<template>
  <form
    ref="form"
    novalidate
    class="container px-0 d-flex flex-column justify-content-between needs-validation"
    @submit="submit"
  >
    <div class="flex-grow-1">
      <div class="row">
        <BootstrapFormControl
          v-model="modelName"
          name="Cost Pool Name"
          class="col"
          required
          :error="errorMessages.get('name')"
          :server-error="serverError"
        />
        <BootstrapFormControl
          v-model="modelCostCenterIdentifier"
          name="Cost Center Identifier"
          class="col"
          required
          :error="errorMessages.get('cost_center_identifier')"
          :server-error="serverError"
        />
      </div>
      <div class="row">
        <BootstrapFormControl
          v-model="modelDescription"
          name="Cost Pool Description"
          class="col"
          required
          :error="errorMessages.get('description')"
          :server-error="serverError"
          textarea
        />
      </div>
      <section class="row">
        <div class="col">
          <div class="col card bg-body-secondary">
            <div class="card-header">
              <h5 class="my-1">Cost Pool Type</h5>
            </div>
            <div class="card-body">
              <div class="container p-0">
                <div class="row">
                  <BootstrapSelect
                    v-model="modelType"
                    name="Cost Pool Type"
                    class="col"
                    :options="typeOptions"
                    required
                    :error="errorMessages.get('cost_pool_type')"
                    :server-error="serverError"
                  />
                </div>
              </div>
              <Transition mode="out-in">
                <div v-if="modelType === 'indirect'" class="container p-0">
                  <div class="row">
                    <BootstrapFormControl
                      v-model="modelUnitType"
                      name="Unit Type"
                      class="col"
                      required
                      :error="errorMessages.get('unit_type')"
                      :server-error="serverError"
                    />
                  </div>
                  <div class="row">
                    <BootstrapFormControl
                      v-model="modelUnitDescription"
                      name="Unit Description"
                      class="col"
                      required
                      :error="errorMessages.get('unit_description')"
                      :server-error="serverError"
                      textarea
                    />
                  </div>
                  <div class="row">
                    <BootstrapFormControl
                      v-model="modelUnitWeightMethod"
                      name="Unit Weight Method"
                      class="col"
                      required
                      :error="errorMessages.get('unit_weight_method')"
                      :server-error="serverError"
                      textarea
                    />
                  </div>
                  <div class="row">
                    <BootstrapFormControl
                      v-model="modelEstimatedCapacity"
                      name="Estimated Capacity"
                      class="col"
                      required
                      :error="errorMessages.get('estimated_capacity')"
                      :server-error="serverError"
                    />
                    <BootstrapFormControl
                      v-model="modelMaxCapacity"
                      name="Max Capacity"
                      class="col"
                      required
                      :error="errorMessages.get('estimated_capacity')"
                      :server-error="serverError"
                    />
                    <BootstrapFormControl
                      v-model="modelPercentageOfCostCenter"
                      name="percentageOfCostCenter"
                      class="col"
                      required
                      :error="errorMessages.get('max_capacity')"
                      :server-error="serverError"
                    />
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>
      </section>
      <div class="row mt-3">
        <div class="d-flex w-100">
          <BootstrapButton
            name="submit"
            class="me-2"
            type="submit"
            :disabled="loading"
            :loading="loading"
            @click.stop
          >
            Save
          </BootstrapButton>
          <BootstrapButton name="cancel" variant="secondary" @click="router.go(-1)">
            Cancel
          </BootstrapButton>
        </div>
      </div>
    </div>
  </form>
</template>
<script lang="ts" setup>
const props = defineProps<{
  costCenterIdentifier: string;
  description: string;
  estimatedCapacity: string;
  errorMessages: Map<any, any>;
  loading: boolean;
  maxCapacity: string;
  name: string;
  percentageOfCostCenter: string;
  typeOptions: string[];
  type: string;
  unitDescription: string;
  unitType: string;
  unitWeightMethod: string;
  serverError: boolean;
}>();

const emit = defineEmits<{
  'update:costCenterIdentifier': [value: string];
  'update:description': [value: string];
  'update:estimatedCapacity': [value: string];
  'update:maxCapacity': [value: string];
  'update:name': [value: string];
  'update:percentageOfCostCenter': [value: string];
  'update:type': [value: string];
  'update:unitDescription': [value: string];
  'update:unitType': [value: string];
  'update:unitWeightMethod': [value: string];
  submit: [form: globalThis.Ref<HTMLFormElement | null>];
}>();

const router = useRouter();

// Template ref
const form = ref<HTMLFormElement | null>(null);

// Models
const modelCostCenterIdentifier = computed({
  get: () => props.costCenterIdentifier,
  set: (value) => emit('update:costCenterIdentifier', value),
});
const modelDescription = computed({
  get: () => props.description,
  set: (value) => emit('update:description', value),
});
const modelEstimatedCapacity = computed({
  get: () => props.estimatedCapacity,
  set: (value) => emit('update:estimatedCapacity', value),
});
const modelMaxCapacity = computed({
  get: () => props.maxCapacity,
  set: (value) => emit('update:maxCapacity', value),
});
const modelName = computed({
  get: () => props.name,
  set: (value) => emit('update:name', value),
});
const modelPercentageOfCostCenter = computed({
  get: () => props.percentageOfCostCenter,
  set: (value) => emit('update:percentageOfCostCenter', value),
});
const modelType = computed({
  get: () => props.type,
  set: (value) => emit('update:type', value),
});
const modelUnitDescription = computed({
  get: () => props.unitDescription,
  set: (value) => emit('update:unitDescription', value),
});
const modelUnitType = computed({
  get: () => props.unitType,
  set: (value) => emit('update:unitType', value),
});
const modelUnitWeightMethod = computed({
  get: () => props.unitWeightMethod,
  set: (value) => emit('update:unitWeightMethod', value),
});

// https://getbootstrap.com/docs/4.6/components/forms/?#how-it-works
const submit = async (event: Event) => {
  if (form.value?.checkValidity() === false) {
    event.preventDefault();
    event.stopPropagation();
    form.value.classList.add('was-validated');
  } else {
    event.preventDefault();
    emit('submit', form);
  }
};
</script>
